import { Link } from 'react-router-dom';
import { recipes } from '../data/recipes';
import { usePlan } from '../lib/plan';
import { pairings } from '../lib/shopping';
import { prevBatch, nextBatch, yieldUnit } from '../lib/scale';
import { ShoppingScript } from '../components/ShoppingScript';
import { MealBadge } from '../components/MealBadge';

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="border-b border-line pb-2 font-sans text-[0.78rem] font-bold uppercase tracking-[0.12em] text-ink-soft">
      {children}
    </h2>
  );
}

// This week: the recipes being cooked, each at its own batch size, merged
// into one Walmart cart. Same ingredient across two recipes = one line.
export default function Plan() {
  const plan = usePlan();
  const picks = pairings(plan.items, recipes);

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
      <div className="pt-8 sm:pt-12">
        <h1 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">This week</h1>
        <p className="mt-2 font-serif text-lg text-ink-soft">
          Pick what’s getting cooked, set each batch size, and get one cart for all of it. Everyone with
          the PIN sees the same plan. It clears itself after a week.
        </p>
      </div>

      {plan.count === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-line bg-card p-6 text-center">
          <p className="font-serif text-xl">Nothing planned yet.</p>
          <p className="mt-1.5 font-serif text-ink-soft">
            Tap <span className="font-sans text-[0.85rem] font-semibold">Add to this week</span> on any recipe{' '}
            <Link to="/" className="underline decoration-dotted underline-offset-4 hover:decoration-solid">in the catalog</Link>.
          </p>
        </div>
      ) : (
        <>
          <section className="mt-8">
            <div className="flex items-end justify-between gap-4">
              <SectionTitle>Cooking</SectionTitle>
              <button
                type="button"
                onClick={plan.clear}
                className="mb-1 h-9 rounded-full border border-line bg-card px-4 font-sans text-[0.78rem] font-semibold text-ink transition-colors hover:border-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                Clear the week
              </button>
            </div>
            <ul className="mt-3 divide-y divide-line/60">
              {plan.items.map(({ recipe, servings, original }) => {
                const current = servings ?? original;
                return (
                  <li key={recipe.slug} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/recipe/${recipe.slug}`}
                        className="font-serif text-[1.15rem] font-semibold leading-snug underline decoration-transparent underline-offset-4 hover:decoration-line"
                      >
                        {recipe.title}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <MealBadge meal={recipe.meal} />
                        <span className="font-sans text-[0.78rem] text-ink-soft">book makes {recipe.yield}</span>
                      </div>
                    </div>
                    {original !== null && (
                      <div className="flex items-center gap-1.5" role="group" aria-label={`Batch size for ${recipe.title}`}>
                        <button
                          type="button"
                          aria-label="Smaller batch"
                          disabled={prevBatch(original, current) === null}
                          onClick={() => {
                            const n = prevBatch(original, current);
                            if (n !== null) plan.setServings(recipe.slug, n === original ? null : n);
                          }}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper font-sans text-xl leading-none text-ink hover:border-ink-soft disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                        >
                          −
                        </button>
                        <span className={`min-w-[6.5rem] text-center font-sans text-[0.95rem] font-semibold ${servings && servings !== original ? 'text-goal' : ''}`}>
                          {current} {yieldUnit(recipe.yield)}
                        </span>
                        <button
                          type="button"
                          aria-label="Bigger batch"
                          disabled={nextBatch(original, current) === null}
                          onClick={() => {
                            const n = nextBatch(original, current);
                            if (n !== null) plan.setServings(recipe.slug, n === original ? null : n);
                          }}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-paper font-sans text-xl leading-none text-ink hover:border-ink-soft disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                        >
                          +
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${recipe.title} from this week`}
                      onClick={() => plan.remove(recipe.slug)}
                      className="flex h-11 w-11 items-center justify-center rounded-full text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          {picks.length > 0 && (
            <section className="mt-10">
              <SectionTitle>Good next picks</SectionTitle>
              <p className="mt-2 font-sans text-[0.8rem] text-ink-soft">
                Recipes that would use up what this week leaves in the fridge.
              </p>
              <ul className="mt-3 grid gap-3 sm:grid-cols-3">
                {picks.map((p) => (
                  <li key={p.recipe.slug} className="rounded-xl border border-line bg-card p-4">
                    <Link to={`/recipe/${p.recipe.slug}`} className="font-serif text-[1.05rem] font-semibold leading-snug underline decoration-transparent underline-offset-4 hover:decoration-line">
                      {p.recipe.title}
                    </Link>
                    <p className="mt-1 font-serif text-[0.88rem] italic leading-snug text-ink-soft">
                      Uses up {p.usesUp.map((s) => s.toLowerCase()).join(', ')}.
                    </p>
                    <button
                      type="button"
                      onClick={() => plan.add(p.recipe.slug)}
                      className="mt-3 h-9 rounded-full border border-line bg-paper px-3.5 font-sans text-[0.8rem] font-semibold text-ink hover:border-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                    >
                      + Add
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-10">
            <SectionTitle>Walmart cart script — the whole week</SectionTitle>
            <ShoppingScript items={plan.items} />
          </section>
        </>
      )}
    </div>
  );
}
